import * as React from 'react'
import { FC } from 'react'
import {
  MoreButton,
  DataItem,
  OverflowDetector,
  PhoneLink,
} from '@sales-center/components'
import { LocationLink } from '@user/components/Location'
import { CardVariants, ContactCardProps } from './typings'
import { useActions, useData, getStyles } from './hooks'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Link from '@mui/material/Link'

export const ContactCard: FC<ContactCardProps> = (props) => {
  const {
    sx,
    contact,
    isPrimaryContact,
    variant,
    canUpdateEntity,
    hideEditOptions = false,
    menuOptions,
  } = props

  const {
    locationCode,
    externalLinkId,
    externalLinkType,
    company,
    phoneNumber,
    phoneExt,
    contactMethod,
    contactTime,
    altPhoneNumber,
    altPhoneExt,
  } = contact

  const {
    onChangePrimaryContact,
    handleOverflow,
    handleShowInfoClick,
    showAll,
    showMoreButton,
  } = useActions(props)

  const {
    contactName,
    contactTimeFormatted,
    contactTypeLabel,
    formattedAddress,
    primaryEmail,
    secondaryEmails,
  } = useData(props)
  const styles = getStyles(props)
  return (
    <Card raised variant="elevation" sx={{ ...styles.root, sx }}>
      <Grid container>
        {isPrimaryContact && (
          <Grid item xs={12}>
            <Typography sx={styles.primaryContactTitle}>
              Primary Contact
            </Typography>
          </Grid>
        )}

        <Grid container item xs={12} sx={styles.title}>
          <Grid sx={styles.name} item xs={6}>
            {contactName}
          </Grid>

          <Grid item xs={6} style={{ textAlign: 'right' }}>
            {canUpdateEntity && !hideEditOptions && (
              <FormControlLabel
                sx={styles.primaryContactFormLabel}
                checked={isPrimaryContact}
                value={true}
                control={<Radio />}
                label="Primary Contact"
                labelPlacement="end"
                onChange={onChangePrimaryContact}
              />
            )}
            {menuOptions && !hideEditOptions && (
              <MoreButton
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                options={menuOptions}
                dataTestId={`contactCardMoreBtn-${contact.id}`}
              />
            )}
          </Grid>
        </Grid>

        {contactTypeLabel && (
          <Grid item xs={12}>
            {contactTypeLabel}
            {!!externalLinkId && (
              <LocationLink
                externalLocationId={externalLinkId}
                externalLocationCode={locationCode}
                hideLocationPrefix
                billingLocation={externalLinkType === 'BillTo'}
              />
            )}
          </Grid>
        )}

        <OverflowDetector
          open={showAll}
          closedHeight={72}
          onOverflowChange={handleOverflow}
          className={styles.overflowDetector}
        >
          <Grid container sx={styles.dataItemContainer} spacing={3}>
            <DataItem isEmpty={!primaryEmail} title="Primary Email" lg={'auto'}>
              <a
                href={`mailto:${primaryEmail}`}
                target="_blank"
                rel="noreferrer"
              >
                {primaryEmail}
              </a>
            </DataItem>

            {secondaryEmails &&
              secondaryEmails.map((secondaryEmail, index) => (
                <DataItem
                  key={index}
                  isEmpty={!secondaryEmail.emailAddress}
                  title="Additional Email"
                  lg={'auto'}
                >
                  <a
                    href={`mailto:${secondaryEmail.emailAddress}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {secondaryEmail.emailAddress}
                  </a>
                </DataItem>
              ))}

            <DataItem title="Main Phone" isEmpty={!phoneNumber}>
              <PhoneLink
                phone={phoneNumber}
                phoneExt={phoneExt}
                includeExtInLink
              />
            </DataItem>

            <DataItem title="Address" lg={'auto'}>
              {formattedAddress || <>&mdash;</>}
            </DataItem>

            <DataItem title="Company Name" isEmpty={!company}>
              {company}
            </DataItem>

            {variant === CardVariants.wwm && (
              <>
                <DataItem title="Best Contact Method" isEmpty={!contactMethod}>
                  {contactMethod || <>-</>}
                </DataItem>

                <DataItem title="Best Contact Time" isEmpty={!contactTime}>
                  {contactTimeFormatted || <>-</>}
                </DataItem>

                <DataItem title="Alternate Phone" isEmpty={!altPhoneNumber}>
                  <PhoneLink
                    phone={altPhoneNumber}
                    phoneExt={altPhoneExt}
                    includeExtInLink={true}
                  />
                </DataItem>
              </>
            )}
          </Grid>
        </OverflowDetector>

        <Grid item xs={12}>
          <Link
            href="#"
            sx={styles.moreInfoLink}
            onClick={handleShowInfoClick}
            style={{
              marginTop: showAll ? '8px' : '0',
              opacity: showMoreButton ? 1 : 0,
            }}
          >
            {showAll ? 'Show Less Information' : 'Show More Information'}
          </Link>
        </Grid>
      </Grid>
    </Card>
  )
}
ContactCard.displayName = 'ContactCard'
